import * as React from 'react';
import { Link } from 'react-router-dom';

import {
	Form,
	Formik,
	Field,
	FieldProps,
} from 'formik';
import { OptGroup } from 'rc-select';

import { Select } from '@app/components/Selects/Select';
import { Option } from '@app/components/Selects/Option';
import { Icon } from '@app/image/icon';
import { ActionMode, Button, ButtonVariant } from '@app/components/Button/Button';

import { useOdesseus } from '@app/odesseus/useOdesseus';
import {
	IFilters,
	TFilter,
	FilterKeys,
	OdesseusSelectOption,
	IRange,
	OdesseusOptions, OdesseusSelectOptionGroup,
} from '@app/odesseus/types/Filters';
import { Nullable } from '@app/objects/Utility';
import { OdyManager } from '@app/odesseus/OdyManager';

import '@app/scss/layout.scss';

interface FormValues {
	[FilterKeys.DURATION]: Array<number>;
	[FilterKeys.DEPARTUREDATE]: Array<number>;
	[FilterKeys.DESTINATION]: Array<string>;
	[FilterKeys.CRUISELINE]: Array<string>;
	[FilterKeys.SHIP]: Array<string>;
	[FilterKeys.DEPARTUREPORT]: Array<string>;
}

const initialValues: FormValues = {
	[FilterKeys.DURATION]: [],
	[FilterKeys.DEPARTUREDATE]: [],
	[FilterKeys.DESTINATION]: [],
	[FilterKeys.CRUISELINE]: [],
	[FilterKeys.SHIP]: [],
	[FilterKeys.DEPARTUREPORT]: [],
};

function addValues<T extends TFilter = TFilter>(
	filters: Array<IFilters<T>>,
	key: string,
	values: Array<T>,
): Array<IFilters<T>> {
	if (!values.length) return filters.filter((item: IFilters<T>) => item.key !== key);

	const entity = filters?.find((item: IFilters<T>) => item.key === key);
	if (!entity) return [...filters, { values, key }];

	return filters.map((item: IFilters<T>) => (item.key === key ? { values, key } : item));
}

function addRanges<T extends TFilter = TFilter>(
	filters: Array<IFilters<T>>,
	key: string,
	ranges: Array<T>,
): Array<IFilters<T>> {
	if (!ranges.length) return filters.filter((item: IFilters<T>) => item.key !== key);

	const entity = filters?.find((item: IFilters<T>) => item.key === key);
	if (!entity) return [...filters, { ranges, key }];

	return filters.map((item: IFilters<T>) => (item.key === key ? { ranges, key } : item));
}

function getDatesRange(values: FormValues, options: OdesseusOptions): { min: number; max: number } {
	const ranges: Array<IRange<number>> = values.departureDateTime.map((item: number) => {
		const value = options.dates.find((q: OdesseusSelectOption<IRange>) => q.value === item);
		if (!value?.raw) return null;

		return value.raw;
	}).filter((q: IRange<number> | null) => q) as Array<IRange<number>>;

	const min = Math.min.apply(null, ranges.map((value: IRange<number>) => value.from));
	const max = Math.max.apply(null, ranges.map((value: IRange<number>) => value.to).filter((q: number | undefined) => q) as Array<number>);

	return { min, max };
}

function getDurations(values: FormValues, options: OdesseusOptions): string {
	return (values.duration
		.map((value: number) => {
			const option = options.durations.find((q: OdesseusSelectOption<IRange<number>>) => q.value === value);
			if (!option?.raw) return null;

			return option.raw;
		})
		.filter((range: Nullable<IRange<number>>) => range) as Array<IRange<number>>)
		.sort((a: IRange<number>, b: IRange<number>) => a.from - b.from)
		.map((range: IRange<number>) => (range.to ? `${range.from}-${range.to}` : range.from))
		.join(',');
}

const baseUrl = 'https://res.onlinevacationcenter.com/swift/cruise';
function goSearch(values: FormValues, options: OdesseusOptions) {
	const params: Array<string> = [
		'siid=222056',
		'searchcruise=1',
		'lang=1',
		'destinationtype=All',
	];

	if (values.cruiselineId.length) {
		params.push(`cruiseline=${values.cruiselineId.join(',')}`);
	}

	if (values.shipId.length) {
		params.push(`ship=${values.shipId.join(',')}`);
	}

	if (values.departurePortCode.length) {
		params.push(`departureports=${values.departurePortCode.join(',')}`);
	}

	if (values.destinationId.length) {
		params.push(`destinations=${values.destinationId.join(',')}`);
	}

	if (values.departureDateTime.length) {
		const { min, max } = getDatesRange(values, options);

		params.push(`startdate=${OdyManager.toDateString(min)}`);
		if (max !== -Infinity) {
			params.push(`enddate=${OdyManager.toDateString(max)}`);
		}
	}

	if (values.duration.length) {
		params.push(`durations=${getDurations(values, options)}`);
	}

	const path = `${baseUrl}?${params.join('&')}`;
	window.open(path);
}

export const SearchCruises: React.FC = () => {
	const [filters, setFilters] = React.useState<Array<IFilters>>([]);
	const [lastChange, setLastChange] = React.useState<Nullable<FilterKeys>>(() => null);
	const { options, loading } = useOdesseus(filters, lastChange);

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values: FormValues) => goSearch(values, options)}
		>
			{
				(formikBag) => (
					<Form id="cruises-search-form" className="layout-container content-halign_center">
						<div className="col-12 col-lg-12 col-xl-9">
							<div className="layout-container">
								<Field name="destinationId">
									{({ field, form }: FieldProps) => (
										<div className="col-12 col-lg-6 col-xl-4 content-valign_bottom">
											<Select<number>
												placeholder="Destination/River"
												Icon={Icon.Location}
												tabIndex={1}
												multiple
												loading={loading}
												maxTagCount={6}
												value={field.value}
												onChange={(values: Array<number>) => {
													form.setFieldValue(field.name, values);
													setFilters(addValues(filters, field.name, values));
													setLastChange(field.name as FilterKeys);
												}}
											>
												{
													options?.destinations.map((option: OdesseusSelectOption) => (
														<Option key={option.value} value={option.value}>
															{option.name}
														</Option>
													))
												}
											</Select>
										</div>
									)}
								</Field>
								<Field name="departurePortCode">
									{({ field, form }: FieldProps) => (
										<div className="col-12 col-lg-6 col-xl-4 content-valign_bottom">
											<Select<number>
												placeholder="Departure Port"
												Icon={Icon.Anchor}
												tabIndex={2}
												loading={loading}
												multiple
												maxTagCount={6}
												value={field.value}
												onChange={(values: Array<number>) => {
													form.setFieldValue(field.name, values);
													setFilters(addValues(filters, field.name, values));
													setLastChange(field.name as FilterKeys);
												}}
											>
												{
													options?.ports.map((option: OdesseusSelectOption) => (
														<Option key={option.value} value={option.value}>
															{option.name}
														</Option>
													))
												}
											</Select>
										</div>
									)}
								</Field>
								<Field name="cruiselineId">
									{({ field, form }: FieldProps) => (
										<div className="col-12 col-lg-6 col-xl-4 content-valign_bottom">
											<Select<number>
												placeholder="Cruise Line/Tour Operator"
												Icon={Icon.Helm}
												tabIndex={4}
												multiple
												loading={loading}
												maxTagCount={6}
												value={field.value}
												onChange={(values: Array<number>) => {
													form.setFieldValue(field.name, values);
													setFilters(addValues(filters, field.name, values));
													setLastChange(field.name as FilterKeys);
												}}
											>
												{
													options?.cruiselines.map((option: OdesseusSelectOption) => (
														<Option key={option.value} value={option.value}>
															{option.name}
														</Option>
													))
												}
											</Select>
										</div>
									)}
								</Field>

								<Field name="shipId">
									{({ field, form }: FieldProps) => (
										<div className="col-12 col-lg-6 col-xl-4 content-valign_bottom">
											<Select<number>
												placeholder="Ships"
												Icon={Icon.Ship}
												tabIndex={4}
												multiple
												loading={loading}
												value={field.value}
												onChange={(values: Array<number>) => {
													form.setFieldValue(field.name, values);
													setFilters(addValues(filters, field.name, values));
													setLastChange(field.name as FilterKeys);
												}}
											>
												{
													options?.ships.map((item: OdesseusSelectOptionGroup) => (
														<OptGroup label={item.title} key={item.title}>
															{item.options.map((option: OdesseusSelectOption) => (
																<Option value={option.value} key={option.value}>
																	{option.name}
																</Option>
															))}
														</OptGroup>
													))
												}
											</Select>
										</div>
									)}
								</Field>
								<Field name="departureDateTime">
									{({ field, form }: FieldProps) => (
										<div className="col-12 col-lg-6 col-xl-4 content-valign_bottom">
											<Select<number>
												placeholder="Travel Dates"
												Icon={Icon.Calendar}
												tabIndex={5}
												multiple
												loading={loading}
												maxTagCount={6}
												value={field.value}
												onChange={(values: Array<number>) => {
													form.setFieldValue(field.name, values);
													const ranges = (
														values.map(
															(item: number) =>
																options.dates.find(
																	(date: OdesseusSelectOption<IRange<number>>) => date.value === item,
																)?.raw,
														).filter((item: IRange<number> | undefined) => item) as Array<IRange<number>>
													).map((item: IRange<number>) => ({
														from: OdyManager.toDateString(item.from),
														to: item.to ? OdyManager.toDateString(item.to) : undefined,
													}));
													setFilters(addRanges(filters, field.name, ranges));
													setLastChange(field.name as FilterKeys);
												}}
											>
												{
													options?.dates.map((option: OdesseusSelectOption<IRange<number>>) => (
														<Option key={option.value} value={option.value}>
															{option.name}
														</Option>
													))
												}
											</Select>
										</div>
									)}
								</Field>
								<Field name="duration">
									{({ field, form }: FieldProps) => (
										<div className="col-12 col-lg-6 col-xl-4 content-valign_bottom">
											<Select<number>
												placeholder="Length"
												Icon={Icon.Moon}
												tabIndex={6}
												multiple
												loading={loading}
												value={field.value}
												onChange={(values: Array<number>) => {
													form.setFieldValue(field.name, values);
													const ranges = (
														values.map(
															(item: number) =>
																options.durations.find(
																	(date: OdesseusSelectOption<IRange<number>>) => date.value === item,
																)?.raw,
														).filter((item: IRange<number> | undefined) => item) as Array<IRange<number>>
													).map((item: IRange<number>) => ({
														from: item.from.toString(),
														to: item.to ? item.to.toString() : undefined,
													}));
													setFilters(addRanges(filters, field.name, ranges));
													setLastChange(field.name as FilterKeys);
												}}
											>
												{
													options?.durations.map((option: OdesseusSelectOption<IRange<number>>) => (
														<Option key={option.value} value={option.value}>
															{option.name}
														</Option>
													))
												}
											</Select>
										</div>
									)}
								</Field>
							</div>
						</div>

						<div className="button-container col-12 col-lg-12 col-xl-3">
							<div className="layout-container content-halign_center">
								<div className="col-12">
									<Link
										to="https://travel.onlinevacationcenter.com/acton/media/12605/special-faqs"
										className="search-card__covid_bottom font-family_accent font-weight_medium text-size_small"
									>
										Travel Alerts
									</Link>
								</div>
								<div className="col-12 col-lg-6 col-xl-12">
									<Button
										style={{ width: '100%' }}
										className="uppercase form-group"
										variant={ButtonVariant.Filled}
										tabIndex={7}
										action={ActionMode.InternalLink}
										link="search"
										onClick={formikBag.handleSubmit}
									>
										Search cruises
									</Button>
								</div>
								<div className="col-12 col-lg-6 col-xl-12">
									<Button
										variant={ButtonVariant.Underlined}
										className="uppercase form-group"
										tabIndex={8}
										action={ActionMode.Button}
										onClick={() => {
											formikBag.resetForm();
											setFilters([]);
											setLastChange(null);
										}}
									>
										Reset all
									</Button>
								</div>
							</div>
						</div>
					</Form>
				)
			}
		</Formik>
	);
};
